import * as THREE from 'three';
import { geoidLayerIsVisible } from "../Layer/GeoidLayer.js";
import { tiledCovering } from "./Tile/Tile.js";
/**
 * A TileMesh is a THREE.Mesh with a geometricError and an OBB.
 * The objectId property of the layered material is assigned to the id of the
 * TileMesh.
 * @param geometry - The tile geometry
 * @param material - A THREE.Material compatible with THREE.Mesh
 * @param layer - The layer the tile is added to
 * @param extent - The tile extent
 * @param level - The tile level (default = 0)
 */
class TileMesh extends THREE.Mesh {
  _tms = (() => new Map())();
  constructor(geometry, material, layer, extent) {
    let level = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
    super(geometry, material);
    if (!extent) {
      throw new Error('extent is mandatory to build a TileMesh');
    }
    this.layer = layer;
    this.extent = extent;
    this.level = level;
    this.material.setUniform('objectId', this.id);
    this.obb = this.geometry.OBB.clone();
    this.boundingSphere = new THREE.Sphere();
    this.obb.box3D.getBoundingSphere(this.boundingSphere);
    for (const tms of layer.tileMatrixSets) {
      this._tms.set(tms, tiledCovering(this.extent, tms));
    }
    this.frustumCulled = false;
    this.matrixAutoUpdate = false;
    this.layerUpdateState = {};
    this.isTileMesh = true;
    this.geoidHeight = 0;
    this.link = {};
    let _visible = true;
    Object.defineProperty(this, 'visible', {
      get() {
        return _visible;
      },
      set(v) {
        if (_visible != v) {
          _visible = v;
          this.dispatchEvent({
            type: v ? 'shown' : 'hidden'
          });
        }
      }
    });
  }

  /**
   * If specified, updates the min and max elevation of the OBB
   * and updates accordingly the bounding sphere and the geometric error.
   *
   * @param elevation - Elevation parameters
   */
  setBBoxZ(elevation) {
    elevation.geoidHeight = geoidLayerIsVisible(this.layer) ? this.geoidHeight : 0;
    this.obb.updateZ(elevation);
    if (this.horizonCullingPointElevationScaled && this.horizonCullingPoint) {
      this.horizonCullingPointElevationScaled.setLength(this.obb.z.delta + this.horizonCullingPoint.length());
    }
    this.obb.box3D.getBoundingSphere(this.boundingSphere);
  }
  getExtentsByProjection(tms) {
    return this._tms.get(tms);
  }

  /**
   * Finds the common ancestor between this tile and another one. It goes
   * through parents on each side until one is found.
   *
   * @param tile - The tile to find common ancestor with
   * @returns The common ancestor between those two tiles, or undefined if
   * not found
   */
  findCommonAncestor(tile) {
    if (!tile) {
      return undefined;
    }
    if (tile.level == this.level) {
      if (tile.id == this.id) {
        return tile;
      } else if (tile.level != 0) {
        return this.parent?.findCommonAncestor(tile.parent);
      } else {
        return undefined;
      }
    } else if (tile.level < this.level) {
      return this.parent?.findCommonAncestor(tile);
    } else {
      return this.findCommonAncestor(tile.parent);
    }
  }

  /**
   * An optional callback that is executed immediately before a 3D object
   * is rendered.
   *
   * @param renderer - The renderer used to render textures.
   */
  onBeforeRender(renderer) {
    if (this.material.layersNeedUpdate) {
      this.material.updateLayersUniforms(renderer);
    }
  }
}
export default TileMesh;